import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Wine, Cake, Sparkles } from 'lucide-react';

const HERO_IMAGE = '/images/soursweet-kokteyl.png';

export const Home = () => {
  const { t, language } = useLanguage();
  const isGerman = language === 'de';

  useEffect(() => {
    document.title = language === 'de' ? 'SourSweet.de/Startseite' : 'SourSweet.de/Home';
  }, [language]);

  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16 sm:pt-20" data-testid="hero-section">
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center md:bg-fixed"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-emerald-900/50 to-emerald-900/80" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full -mt-8 sm:-mt-12 md:-mt-16">
          <div className="max-w-xl ml-auto text-center">
            <div className="mb-2 sm:mb-3">
              <Sparkles className="inline-block text-lime-300" size={24} />
            </div>

            <h1
              className="font-heading text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white tracking-tight mb-2 sm:mb-3 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>

            <p
              className="text-xs sm:text-sm md:text-base text-lime-100 mb-6 font-semibold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
              data-testid="hero-subtitle"
            >
              {t('hero.subtitle')}
            </p>

            <p className="text-lime-100 text-sm sm:text-base md:text-lg leading-relaxed font-semibold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              {isGerman 
                ? 'Mit SourSweet bringen wir nicht nur Getränke, sondern Atmosphäre zu Ihrem Event. Wir kombinieren Festival-Vibes, hochwertige Cocktails und schokoladenüberzogene Erdbeeren in einem stilvollen mobilen Bar-Konzept – direkt bei Ihnen vor Ort.'
                : 'With SourSweet, we bring more than drinks – we bring atmosphere to your event. Combining festival vibes, premium cocktails, and chocolate-covered strawberries, our stylish mobile bar concept delivers a unique experience directly to you.'}
            </p>
            <p className="text-lime-100 text-sm sm:text-base md:text-lg leading-relaxed font-semibold mt-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              {isGerman 
                ? 'SourSweet vereint zwei besondere Konzepte unter einer Marke: Sour steht für unsere energiegeladenen, hochwertigen Cocktails, während Sweet unsere genussvollen und eleganten Dessert-Kreationen repräsentiert.'
                : 'SourSweet unites two distinctive concepts under one brand: Sour represents our bold, energetic premium cocktails, while Sweet stands for our refined and indulgent dessert creations.'}
            </p>
          </div>
        </div>
      </section>

      {/* Unsere Geschichte Section - Dark Theme */}
      <section className="pt-6 pb-4 sm:pt-8 sm:pb-6 bg-emerald-900" data-testid="story-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              {isGerman ? 'Unsere Geschichte' : 'Our Story'}
            </h2>
            <div className="w-16 h-[2px] bg-yellow-400 mx-auto" />
          </div>
          
          <div className="space-y-6 text-center">
            <p className="text-lime-100 text-base sm:text-lg leading-relaxed font-semibold">
              {isGerman 
                ? 'SourSweet entstand aus einer einfachen, aber starken Idee: Genuss und Erlebnis zu vereinen.'
                : 'SourSweet began with a simple yet powerful idea: bringing people together through taste and atmosphere.'}
            </p>
            <p className="text-lime-100 text-base sm:text-lg leading-relaxed font-semibold">
              {isGerman 
                ? 'Was als mobiles Cocktail- und Dessert-Konzept begann, entwickelte sich schnell zu einem besonderen Highlight für Events jeder Art. Unsere Marke basiert auf zwei Charakteren: dem mutigen, lebendigen Sour und dem warmen, genussvollen Sweet.'
                : 'What started as a mobile cocktail and dessert concept quickly evolved into a standout highlight for events of all kinds. Our brand is built on two characters: the bold and vibrant Sour, and the warm and indulgent Sweet.'}
            </p>
            <p className="text-lime-100 text-base sm:text-lg leading-relaxed font-semibold">
              {isGerman 
                ? 'Inspiriert von der Festival-Atmosphäre verwandeln wir mit Premium-Cocktails und schokoladenüberzogenen Erdbeeren jede Veranstaltung in einen besonderen Moment. Wir servieren nicht nur Getränke und Desserts – wir schaffen Erinnerungen, die Menschen verbinden.'
                : 'Inspired by festival energy, we transform events into memorable moments with premium cocktails and chocolate-covered strawberries. We don\'t just serve drinks and desserts – we create experiences that connect people.'}
            </p>
            <p className="text-yellow-400 text-lg sm:text-xl font-bold mt-8">
              {isGerman 
                ? 'SourSweet ist mehr als eine Marke – es ist ein Erlebnis.'
                : 'SourSweet is more than a brand – it\'s an experience.'}
            </p>
          </div>
        </div>
      </section>

      {/* Cards Section - Dark Theme */}
      <section className="pt-4 pb-16 bg-emerald-800" data-testid="cards-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Sour Card */}
            <Link
              to="/sour"
              data-testid="sour-card"
              className="group flex flex-col bg-emerald-900 border border-emerald-700 rounded-2xl overflow-hidden hover:border-yellow-300 hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] transition-all duration-300"
            >
              <div className="relative h-40 overflow-hidden bg-emerald-900 flex-shrink-0">
                <img
                  src="/images/sour-card-hero.jpg"
                  alt="Cocktails"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/80 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <div className="flex items-center gap-2">
                    <Wine className="text-lime-300" size={22} />
                    <span className="font-heading text-lg font-semibold text-white">{t('about.sourTitle')}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between min-h-[80px]">
                <p className="text-lime-100 text-sm font-semibold">{t('about.sourDesc')}</p>
                <div className="flex items-center gap-2 text-yellow-400 group-hover:gap-3 transition-all text-sm font-bold mt-2">
                  <span className="font-bold">Mehr erfahren</span>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Sweet Card */}
            <Link
              to="/sweet"
              data-testid="sweet-card"
              className="group flex flex-col bg-emerald-900 border border-emerald-700 rounded-2xl overflow-hidden hover:border-yellow-300 hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] transition-all duration-300"
            >
              <div className="relative h-40 overflow-hidden bg-yellow-900 flex-shrink-0">
                <img
                  src="/images/sweet-card-hero.jpeg"
                  alt="Chocolate Strawberries"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-900/80 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <div className="flex items-center gap-2">
                    <Cake className="text-yellow-200" size={22} />
                    <span className="font-heading text-lg font-semibold text-white">{t('about.sweetTitle')}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between min-h-[80px]">
                <p className="text-lime-100 text-sm font-semibold">{t('about.sweetDesc')}</p>
                <div className="flex items-center gap-2 text-yellow-400 group-hover:gap-3 transition-all text-sm font-bold mt-2">
                  <span className="font-bold">Mehr erfahren</span>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Theme */}
      <section className="py-16 bg-emerald-950" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              {t('contact.subtitle')}
            </h2>
            <Link
              to={language === 'de' ? '/reservierung' : '/reservation'}
              data-testid="cta-button"
              className="inline-flex items-center gap-2 bg-yellow-400 text-emerald-950 border border-yellow-300 hover:bg-yellow-300 hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] transition-all duration-300 rounded-full px-8 py-3 font-bold"
            >
              {t('hero.cta')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
